import {
  ArrowLeft,
  MoreHorizontal,
  Star,
  Edit3,
  Archive,
  Users,
  // UsersRound,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import { useEffect, useState } from 'react';

// Hook to force re-render on language change
const useForceUpdate = () => {
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const onLanguageChanged = () => setForceUpdate(n => n + 1);
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => i18nInstance.off('languageChanged', onLanguageChanged);
  }, []);
};

// 🔥 角色配置（带边框颜色）- 使用翻译
const getRoleConfig = (t: Function) => ({
  owner: {
    label: t('roles.owner'),
    color: 'bg-purple-100 text-purple-700',
    borderColor: 'border-purple-200',
  },
  manager: {
    label: t('roles.manager'),
    color: 'bg-blue-100 text-blue-700',
    borderColor: 'border-blue-200',
  },
  editor: {
    label: t('roles.editor'),
    color: 'bg-green-100 text-green-700',
    borderColor: 'border-green-200',
  },
  viewer: {
    label: t('roles.viewer'),
    color: 'bg-gray-100 text-gray-600',
    borderColor: 'border-gray-200',
  },
});

interface ProjectHeaderProps {
  projectName: string;
  projectDescribe: string;
  projectRole?: string; // 🔥 用户在项目中的角色
  canManageProject?: boolean; // 🔥 是否可以管理项目（编辑项目信息、归档等）
  isStarred: boolean;
  isStarring: boolean;
  isArchived: boolean;
  isArchiving: boolean;
  onBackToProjects?: () => void;
  onToggleStar: () => void;
  onToggleArchive: () => void;
  onEditProject: () => void;
  onShowMembers: () => void;
  onCreateGroupChat?: () => void;
}

export default function ProjectHeader({
  projectName,
  projectDescribe,
  projectRole,
  canManageProject = true,
  isStarred,
  isStarring,
  isArchived,
  isArchiving,
  onBackToProjects,
  onToggleStar,
  onToggleArchive,
  onEditProject,
  onShowMembers,
  // onCreateGroupChat,
}: ProjectHeaderProps) {
  const { t } = useTranslation();
  useForceUpdate();

  // 获取角色配置
  const ROLE_CONFIG = getRoleConfig(t);
  const roleConfig = projectRole
    ? ROLE_CONFIG[projectRole as keyof typeof ROLE_CONFIG]
    : null;

  return (
    <>
      {/* Back to projects - 移动端隐藏（底部 Tab Bar 已有 Projects 入口） */}
      <div className="flex-shrink-0 mb-2 sm:mb-4 hidden sm:block">
        <button
          onClick={onBackToProjects}
          className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
        >
          <ArrowLeft size={14} />
          {t('project.allProjects')}
        </button>
      </div>

      {/* Project Title Row */}
      <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-3">
        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
          <span className="text-base sm:text-xl font-semibold text-gray-800 break-words min-w-0">
            {projectName}
          </span>
          {/* 🔥 Role Tag（带边框） */}
          {roleConfig && (
            <span
              className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${roleConfig.color} ${roleConfig.borderColor}`}
            >
              {roleConfig.label}
            </span>
          )}
          {/* 🔥 Archive Tag */}
          {isArchived && (
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border bg-orange-100 text-orange-700 border-orange-200">
              {t('project.archived')}
            </span>
          )}
        </div>
        <div className="flex items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1.5 text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none data-[state=open]:bg-[#f0eee6]">
                <MoreHorizontal size={18} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {/* 🔥 只有 owner/manager 可以编辑项目详情 */}
              <DropdownMenuItem
                onClick={onEditProject}
                disabled={!canManageProject}
                className={
                  !canManageProject
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer'
                }
              >
                <Edit3 size={16} className="mr-2" />
                {t('project.editDetails')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onShowMembers}
                className="cursor-pointer"
              >
                <Users size={16} className="mr-2" />
                {t('project.members')}
              </DropdownMenuItem>
              {/* 🔥 只有 owner/manager 可以归档项目 */}
              {/* 🔥 Archive/Unarchive 按钮只检查角色，不检查 archived 状态 */}
              <DropdownMenuItem
                onClick={onToggleArchive}
                disabled={
                  isArchiving ||
                  !(projectRole === 'owner' || projectRole === 'manager')
                }
                className={
                  !(projectRole === 'owner' || projectRole === 'manager')
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer'
                }
              >
                <Archive size={16} className="mr-2" />
                {isArchived ? t('project.unarchive') : t('common.archive')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* <button
            onClick={onCreateGroupChat}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-800"
            title="Start group chat from this project"
          >
            <UsersRound size={18} />
          </button> */}
          {/* Star 按钮 - 所有角色都可以使用，但 archived 项目禁用 */}
          <button
            onClick={onToggleStar}
            disabled={isStarring || isArchived}
            className={`p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-50 ${
              isStarred
                ? 'text-yellow-500 hover:text-yellow-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            {isStarring ? (
              <div className="animate-spin rounded-full h-[18px] w-[18px] border-b-2 border-gray-400"></div>
            ) : (
              <Star
                size={18}
                className="text-yellow-500"
                fill={isStarred ? 'currentColor' : 'none'}
              />
            )}
          </button>
        </div>
      </div>
      {projectDescribe && (
        <p className="text-sm mb-1 sm:mb-4 text-gray-700 line-clamp-1 sm:line-clamp-none">
          {projectDescribe}
        </p>
      )}
    </>
  );
}
